import { useEffect } from 'react';
import { getConfig } from '~/org/twilio/one-support-center/shared/osc-env';

import { constants } from './definitions';
import { setGlobalUserData, setChatWidgetScript } from './helpers';
import { useIdle, useUserDetails } from '../../hooks';
import { EIGHT_HOURS_MS } from '../../definitions/constants';

/**
 * Fetches the chat widget script and sets the global user data on window for the chat widget script to consume
 *  - Only available to users who are:
 *     - logged in
 *     - have paid support plans (server handles the logic for this)
 */
function useFetchChatWidget(): void {
  const { account, csrf, isLoggedIn, user, refetchCsrf } = useUserDetails();
  const { chatWidgetSource, realm } = getConfig();

  // If the user is idle for 8HRS, re-fetch the CSRF token
  const isIdle = useIdle({
    isDisabled: !isLoggedIn,
    timeToIdle: EIGHT_HOURS_MS,
    onIdle: () => {
      if (refetchCsrf) {
        refetchCsrf();
      }
    },
  });

  useEffect(() => {
    // Required conditions for chat widget script to load;
    const hasUserDetails = account && user;
    const hasExistingChatWidgetScript = document.querySelector(
      constants.scriptQuerySelector(chatWidgetSource),
    );
    const shouldLoadChatWidgetScript =
      isLoggedIn && hasUserDetails && !hasExistingChatWidgetScript;

    if (shouldLoadChatWidgetScript) {
      // Set global user data on window for chat widget bundled script to consume
      setGlobalUserData({ account, csrf, user, realm });

      // Set the chat widget script on the DOM
      const chatWidgetScript = setChatWidgetScript({
        src: chatWidgetSource,
      });

      // On unmount, remove the chat widget script from the DOM
      return () => {
        document.body.removeChild(chatWidgetScript);
      };
    }
  }, [isLoggedIn]);

  // If the user is idle, set the global user data on window with updated CSRF token for the chat widget script to consume
  useEffect(() => {
    const hasUserDetails = account && user;

    if (isIdle && isLoggedIn && hasUserDetails) {
      setGlobalUserData({ account, csrf, user, realm });
    }
  }, [isLoggedIn, csrf]);
}

export default useFetchChatWidget;
